import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import querystring from 'query-string';
import Cookie from 'js-cookie';

import {
    auth,
    firebaseProviderIds,
    signout,
    getProviderIdFromUser
} from 'features/login/utils/firebaseManager';
import {
    setIsLogin,
    setUserInfo,
    setVerifyEmailInfo,
    setLoginSignUpErrorType,
    loginSignUpErrorTypes,
    resetOtpState
} from 'store/module/firebase';
import { queryStrKeys, verifyEmailTypes } from 'features/login/utils/config';
import {
    getUserAccountData,
    getIsHasOldAccountInfoApi,
    createAccountApi,
    updateAccountEmail
} from 'api/account';
import { getUserSponsors, getSponsor, bindingECommerceSponsor } from 'api/sponsor';
import getTurnstileTokenHeadless from 'features/common/Turnstile/getTurnstileTokenHeadless';
import {
    permissionDeniedReasons,
    isKnownPermissionDeniedReason
} from 'config/permissionDeniedReasons';
import { getPartnerSiteConfig, getUserPartner } from 'api/partner';
import { initAccount } from 'store/module/account';
import { initPartner } from 'store/module/partner';
import { initSponsor } from 'store/module/sponsor';
import { initSponsorList, appendSponsorIfNotInList } from 'store/module/sponsors';
import { initAvailablePlanItems } from 'store/module/availablePlanItems';
import { switchLocale } from 'store/module/i18n';
import { setAppInited, setAppState } from 'store/module/app';
import { gtmPushInitData, gtmPushLogin } from 'utils/tracking';
import { objFirstLevelJSONParse } from 'utils/objectManipulate';
import { cookieKeys } from 'config/cookie';
import { getRoleTypeByRoleName, ON_SITE_STAFF } from 'config/roleType';
import useApiFailedHandler from 'features/webhooks/useApiErrorHandler';

import useHistoryWithCurrentSearchHash from 'features/login/hooks/useHistoryWithCurrentSearchHash';
import useCheckIsValidEmailDomain from 'features/login/hooks/useCheckIsValidEmailDomain';
import { FEVER_API_ENDPOINT } from 'config/envVariable';
import { basenameRouteKeys, adminRouteKeys } from 'config/permissionsFunctionMapping';

import Cookies from 'js-cookie';

export default function useInitAndRedirectApp() {
    const dispatch = useDispatch();
    const redirectApp = useRedirectApp();
    const { isInited } = useSelector(s => s.app);
    const [isPartnerSiteConfigInited, setPartnerSiteConfigInited] = useState(false);
    const [isFirebaseLoginInited, setFirebaseLoginInited] = useState(false);

    useEffect(() => {
        async function init() {
            const resp = await getPartnerSiteConfig();
            dispatch(setAppState(objFirstLevelJSONParse(resp)));
            setPartnerSiteConfigInited(true);
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isPartnerSiteConfigInited) {
            const needInitApp = !pathsNoNeedAppInit();
            if (needInitApp) {
                const unSubscribe = onAuthStateChanged(auth, async user => {
                    try {
                        if (user) {
                            dispatch(setIsLogin(Boolean(user)));
                            dispatch(
                                setUserInfo({
                                    name: user.displayName,
                                    email: user.email,
                                    avatarSrc: user.photoURL
                                })
                            );
                            const signInMethods = getProviderIdFromUser();

                            const idTokenResult = await user.getIdTokenResult();
                            const signInProvider = idTokenResult?.signInProvider;

                            dispatch(
                                setUserInfo({
                                    signInMethods,
                                    signInProvider
                                })
                            );
                        }
                        dispatch(setIsLogin(Boolean(user)));
                    } catch (error) {
                        console.error('Init', error);
                    } finally {
                        setFirebaseLoginInited(true);
                    }
                });
                return () => unSubscribe();
            } else {
                dispatch(setAppInited());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPartnerSiteConfigInited]);

    useEffect(() => {
        async function redirect() {
            if (!pathsShouldNotBeRedirectedAtFirst.has(window.location.pathname)) {
                await redirectApp();
            }
            dispatch(setAppInited());
        }
        if (isFirebaseLoginInited) {
            redirect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFirebaseLoginInited]);

    return { isInited };
}

export function useRedirectApp() {
    const dispatch = useDispatch();
    const history = useHistoryWithCurrentSearchHash();
    const location = useLocation();
    const isValidEmailDomain = useCheckIsValidEmailDomain();
    const apiFailedHandler = useApiFailedHandler();

    const { inviteCode, inviteRole } = useSelector(s => s.app);
    const { otpVerification } = useSelector(s => s.firebase);

    async function redirectApp(data) {
        const isLogin = auth.currentUser !== null;
        const emailVerified = isLogin && auth.currentUser.emailVerified;
        const email = isLogin && auth.currentUser.email;

        if (isLogin) {
            if (!emailVerified) {
                if (
                    Boolean(
                        auth.currentUser.providerData.find(
                            p => p.providerId === firebaseProviderIds.PASSWORD
                        )
                    )
                )
                    toVerifiedUpdateEmailPage(dispatch, history, email);
                else {
                    // MISSING_IDENTIFIER
                    if (window.location.href.includes('provider_id')) {
                        toEmailVerifyLandingPage(history);
                    } else {
                        toVerifiedEmailPage(dispatch, history);
                    }
                }
            } else {
                try {
                    // 登入成功，且 OTP 已經驗證成功
                    await initFeverUserData(data);
                } catch (error) {
                    apiFailedHandler(error);
                }
            }
        } else {
            // 使用者未登入，確保重置 OTP 狀態
            dispatch(resetOtpState());
            const { search } = window.location;
            const { redirectAction } = querystring.parse(search);
            switch (true) {
                case redirectAction === 'updateEmailComplete':
                    toUpdateEmailCompletePage(history);
                    break;
                default:
                    toSignInPage(history);
                    break;
            }
        }
    }

    const toInvalidDomainErrorPage = useCallback(async () => {
        await signout();
        dispatch(setLoginSignUpErrorType(loginSignUpErrorTypes.INVALID_EMAIL_DOMAIN));
        history.push({ pathname: paths.ERROR });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const getSponsorAndHandleNoPermission = useCallback(
        async sponsors => {
            let result = {};
            const { pathname: currentPathname, search } = window.location;
            const { sponsorId: sponsorIdFromQs, state: sponsorIdFromState } = querystring.parse(
                search
            );
            // 從 shopline 後台去建立 sponsor 的過程中，此 user 不需要透過一建立 sponsor 的流程建立 sponsor，所以要繞過
            if (sponsors.length <= 0 && !Boolean(sponsorIdFromState)) {
                return toCreateSponsorPage(history);
            }

            let sponsorIndex = 0;
            // sponsorId 權重是 state in url > sponsorId in url > sponsor in cookie > sponsor list 第一筆
            const isOnSiteStaffRole = roleName => getRoleTypeByRoleName(roleName) === ON_SITE_STAFF;
            const retrieveAdminRoleFromSponsorsList = async (sponsors, index) => {
                console.log('retrieveAdminRoleFromSponsorsList:', index);

                if (!Boolean(sponsors[index])) return toCreateSponsorPage(history);
                const currentSponsor = sponsors[index];
                console.log('currentSponsor:', currentSponsor);
                let response = await getSponsor(currentSponsor?.id);
                console.log('response:', response);
                console.log('inviteCode:', inviteCode);
                console.log('currentPathname:', currentPathname);
                console.log(
                    'isOnSiteStaffRole:',
                    isOnSiteStaffRole(response?.sponsor?.accountRole?.roleName)
                );
                // sponsor A 訪問 sponsor B 頁面遇到 403 權限不足，倒轉去 "無使用權限" 頁面。
                // OTP 未驗證的 403 例外：redirectIfInvalid 已經導去 OTP 驗證頁，
                // 這裡不能再導無權限頁把它蓋掉（WorldGym 門市小幫手回報的就是這個情境）
                if (response?.httpStatus === 403 && !response.isOtpValidationFailed) {
                    toPermissionNotDeniedPage(history, resolveSponsor403Reason(response));
                }
                // sponsor list 裡的 id 在後端查無資料（已被刪除）：換下一個 sponsor 也救不了這個連結，
                // 直接讓使用者看到「主辦單位不存在」而不是「無使用權限」
                if (response?.isSponsorNotExist) {
                    toPermissionNotDeniedPage(history, permissionDeniedReasons.SPONSOR_NOT_EXIST);
                    return response;
                }
                // sponsor A 訪問 sponsor B 頁面遇到此 sponsor 是 on site staff，就往下檢查下一個 sponsor 是否有 admin 權限能讓我去訪問 dashboard
                // 如果不是透過邀請連結的 On-site-staff 權限線進入 dashboard 會去 403 頁面，
                // 有邀請連結的話(inviteCode)，會自動倒轉到 on site tool 頁面
                // 從 403 無權限到 app/login 頁面，若發現此 user 無建立過自己的 sponsor，只有被人邀請當 On-site-staff ，就直接帶他送去建立 sponsor 頁面
                if (
                    !Boolean(inviteCode) &&
                    [paths.LOGIN, paths.OTP_VERIFY].includes(currentPathname) &&
                    isOnSiteStaffRole(response?.sponsor?.accountRole?.roleName)
                ) {
                    sponsorIndex++;
                    return retrieveAdminRoleFromSponsorsList(sponsors, sponsorIndex);
                }
                // sponsor A 訪問 sponsor B 頁面遇到 非預期錯誤，用其他 sponsor id 再 call 一次
                if (response === null) {
                    const fallbackSponsorId = sponsors[0]?.id;
                    response = await getSponsor(fallbackSponsorId);
                }
                return response;
            };

            const currentSelectedSponsorId = await (async () => {
                const latestSelectedSponsorId = Cookie.get(cookieKeys.LATEST_SELECTED_SPONSOR_ID);
                switch (true) {
                    case Boolean(sponsorIdFromState && !sponsorIdFromQs):
                        const sponsorId = await bindingECommerceSponsor({
                            state: sponsorIdFromState
                        });
                        return sponsorId;
                    case Boolean(sponsorIdFromQs):
                        return sponsorIdFromQs;
                    case Boolean(latestSelectedSponsorId):
                        return latestSelectedSponsorId;
                    default:
                        return null;
                }
            })();
            if (currentSelectedSponsorId === null) {
                result = retrieveAdminRoleFromSponsorsList(sponsors, sponsorIndex);
            } else {
                result = await getSponsor(currentSelectedSponsorId);
                // cookie 記到的 sponsorId 已查無角色（被移出／過期）：清掉 cookie，
                // 否則使用者每次進站都會用同一個壞掉的 sponsorId 打 API，永遠卡在無權限頁
                const isSponsorIdFromCookie = !sponsorIdFromState && !sponsorIdFromQs;
                if (
                    result?.httpStatus === 403 &&
                    result.isRoleAuthoritiesFailed &&
                    isSponsorIdFromCookie
                ) {
                    Cookie.remove(cookieKeys.LATEST_SELECTED_SPONSOR_ID);
                }
                // 網址／cookie 上的 sponsorId 在後端不存在：同樣要清 cookie（否則每次進站都卡在同一個壞 id），
                // 並帶 reason 導轉，讓使用者知道是「主辦單位不存在」而不是自己沒權限
                if (result?.isSponsorNotExist) {
                    if (isSponsorIdFromCookie) {
                        Cookie.remove(cookieKeys.LATEST_SELECTED_SPONSOR_ID);
                    }
                    toPermissionNotDeniedPage(history, permissionDeniedReasons.SPONSOR_NOT_EXIST);
                    return result;
                }
                if (isOnSiteStaffRole(result?.sponsor?.accountRole?.roleName)) {
                    Cookie.remove(cookieKeys.LATEST_SELECTED_SPONSOR_ID);
                    result = retrieveAdminRoleFromSponsorsList(sponsors, sponsorIndex);
                }
            }

            return result;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inviteCode]
    );

    const initFeverUserData = useCallback(
        async data => {
            try {
                const { pathname: currentPathname, search } = window.location;

                // 在無權限頁不需要執行初始化邏輯
                if (currentPathname === paths.PERMISSION_DENIED) {
                    return;
                }

                const { refUrl, merchant_id: merchantId } = querystring.parse(search);
                let account = await getUserAccountData();
                if (!account) {
                    if (isValidEmailDomain(auth.currentUser.email)) {
                        const hasOldAccountData = await getIsHasOldAccountInfoApi().then(
                            ({ exist } = {}) => exist
                        );
                        if (hasOldAccountData) {
                            // recover old account data and overwrite email
                            // 非互動自動路徑：以無頭方式背景取 token（正常使用者可取得；降級為 null/undefined 時不帶 token，
                            // 依賴後端於無 X-Cfts-Token 時放行，避免少數例外把登入初始化擋死）
                            const turnstileToken = await getTurnstileTokenHeadless();
                            await createAccountApi(
                                {
                                    inviteToken: inviteCode,
                                    settings: { email: auth.currentUser.email }
                                },
                                turnstileToken
                            );
                            account = await getUserAccountData();
                        }
                        if (!account) {
                            return toCreateAccountPage(history);
                        }
                    } else {
                        return toInvalidDomainErrorPage();
                    }
                }
                if (account) {
                    dispatch(initAccount(account));
                }

                const partner = await getUserPartner();
                if (partner) {
                    dispatch(initPartner(partner));
                }

                if (currentPathname === paths.LOGIN) {
                    gtmPushLogin({ partner, account });
                }
                const sponsors = await getUserSponsors();
                const hasAnySponsors = Array.isArray(sponsors) && sponsors.length !== 0;
                if (
                    data?.action === 'doLogin' &&
                    hasAnySponsors &&
                    auth.currentUser.email !== account.email
                ) {
                    try {
                        // 非互動自動路徑：無頭取 token；本區塊本就 try/catch 吞錯不阻塞登入，降級無 token 亦安全
                        const turnstileToken = await getTurnstileTokenHeadless();
                        await updateAccountEmail({
                            sponsorId: sponsors[0].id,
                            adminUuid: account.uuid,
                            body: { email: auth.currentUser.email },
                            turnstileToken
                        });
                    } catch (error) {
                        console.error('update Account Email Error', error);
                    }
                }

                if (data?.action === 'doLogin') {
                    Cookies.set('last_login_host', window.location.host, {
                        domain: '.feversocial.com'
                    });
                }

                // 剛輸入建立完自己的 admin + 是被邀請到他人的 sponsor 做編輯修改，所以 sponsor list 有資料，但是自己並沒有建立自己的 sponsor
                // 已建立完自己的 admin + 是被邀請到他人的 sponsor 做編輯修改，所以 sponsor list 有資料，但是自己並沒有建立自己的 sponsor
                // 已建立完自己的 admin + 是被邀請到他人的 sponsor 做編輯修改 + 已建立自己的 sponsor，所以 sponsor list 有資料
                if (account && hasAnySponsors && Boolean(refUrl)) {
                    return (window.location.href = refUrl);
                }

                if (currentPathname === paths.CREATE_INVITATION) {
                    return;
                } else if (Boolean(refUrl) && refUrl.includes(paths.CREATE_INVITATION)) {
                    return (window.location.href = refUrl);
                }

                // 有 merchantId 就知道是要走後端建立 sponsor 流程，帶此user去授權拿 state，在用 state 換新的 sponsor Id 或是若已換過就直接登入進後台
                if (merchantId) {
                    const platform = window.location.hostname.split('.')[0];
                    // access 此變數 在 domain 是 dev.feversocial.com、local.feversocial.com、feversocial.com 時，直接給 shopline 是因為在開發時，我要傳
                    // shopline 作為 platform 給 API 當參數，但此情形只有 shopline.feversocial.com 能符合，待在上 prod 前要能 work，此行需要寫死
                    const access = ['dev', 'local', 'feversocial'].includes(platform)
                        ? 'shopline'
                        : platform;
                    return toThirdPartyOAuthInitFromEC(access, search);
                }

                if (!hasAnySponsors) {
                    const { state } = querystring.parse(search);
                    // 確定不是要走後端建立 sponsor 流程
                    if (!state && !merchantId) {
                        return toCreateSponsorPage(history);
                    }
                }

                if (
                    inviteRole === ON_SITE_STAFF &&
                    currentPathname !== `/admin${adminRouteKeys.ON_SITE_ASSISTANT}`
                ) {
                    history.replace({
                        ...location,
                        pathname: `/admin${adminRouteKeys.ON_SITE_ASSISTANT}`
                    });
                }

                // sponsor 跟 role OK 都要是 200 ，response 才有值
                let response = await getSponsorAndHandleNoPermission(sponsors);

                if (response?.sponsor !== undefined) {
                    gtmPushInitData({ partner, account, sponsor: response.sponsor });
                    dispatch(initSponsor(response.sponsor));
                    dispatch(initAvailablePlanItems(response.availablePlanItems));
                    dispatch(initSponsorList(appendSponsorIfNotInList(sponsors, response.sponsor)));
                    dispatch(switchLocale(account.language));
                    redirectAfterDataInited(history);
                } else {
                    gtmPushInitData({ partner, account });
                    dispatch(initPartner(partner));
                    dispatch(switchLocale(account.language));
                }
            } catch (error) {
                throw error;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            dispatch,
            getSponsorAndHandleNoPermission,
            inviteCode,
            inviteRole,
            isValidEmailDomain,
            location,
            toInvalidDomainErrorPage
        ]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(redirectApp, [dispatch, initFeverUserData, otpVerification.isVerified]);
}

export const APP_MAIN_PATH = `${basenameRouteKeys.ADMIN}${adminRouteKeys.DASHBOARD}`;
export const paths = {
    LOGIN: '/app/login',
    SITE_LOGIN: '/app/site/login',
    SIGN_UP: '/app/signup',
    OTP_VERIFY: '/app/otp_verify',
    EMAIL_VERIFY: '/app/email_verify',
    EMAIL_VERIFY_LANDING: '/app/email_verify_landing',
    UPDATE_EMAIL_COMPLETE: '/app/update_email_complete',
    ERROR: '/app/error',
    PERMISSION_DENIED: '/app/permission_denied',
    CREATE_ACCOUNT: '/create/account',
    CREATE_SPONSOR: '/create/sponsor',
    CREATE_INVITATION: '/create/invitation',
    UTILS_IMG_READER: '/utils/img_reader'
};
export const pathsShouldNotSetAsRefUrl = new Set([
    paths.LOGIN,
    paths.SITE_LOGIN,
    paths.SIGN_UP,
    paths.OTP_VERIFY,
    paths.CREATE_ACCOUNT,
    paths.CREATE_SPONSOR,
    paths.EMAIL_VERIFY,
    paths.EMAIL_VERIFY_LANDING,
    paths.ERROR,
    paths.UPDATE_EMAIL_COMPLETE,
    paths.PERMISSION_DENIED,
    paths.OTP_VERIFY
]);
const pathsAvailableWhenNoLogin = new Set([paths.LOGIN, paths.SITE_LOGIN, paths.SIGN_UP]);
const pathsShouldNotBeRedirectedAtFirst = new Set([paths.EMAIL_VERIFY_LANDING]);

function redirectAfterDataInited(history) {
    const { pathname, search } = window.location;
    if (
        pathsAvailableWhenNoLogin.has(pathname) ||
        pathname === paths.EMAIL_VERIFY_LANDING ||
        pathname === paths.OTP_VERIFY
    ) {
        history.replace({
            pathname: APP_MAIN_PATH,
            search
        });
    }
}
function pathsNoNeedAppInit() {
    const { pathname } = window.location;
    return pathname.startsWith('/utils');
}

export function toSignInPage(history) {
    const { pathname } = window.location;
    if (!pathsAvailableWhenNoLogin.has(pathname)) {
        history.replace({
            pathname: paths.LOGIN,
            search: getQueryStringHandleRefUrl()
        });
    }
}

function toVerifiedEmailPage(dispatch, history) {
    dispatch(
        setVerifyEmailInfo({
            verifyEmailType: verifyEmailTypes.EMAIL_VERIFY
        })
    );
    const { pathname } = window.location;
    if (pathname !== paths.EMAIL_VERIFY) {
        history.replace({
            pathname: paths.EMAIL_VERIFY,
            search: getQueryStringHandleRefUrl()
        });
    }
}

function toEmailVerifyLandingPage(history) {
    const { pathname } = window.location;
    if (pathname !== paths.EMAIL_VERIFY) {
        history.replace({
            pathname: paths.EMAIL_VERIFY_LANDING,
            search: getQueryStringHandleRefUrl()
        });
    }
}

function toUpdateEmailCompletePage(history) {
    const { pathname } = window.location;
    if (pathname !== paths.EMAIL_VERIFY) {
        history.replace({
            pathname: paths.UPDATE_EMAIL_COMPLETE,
            search: getQueryStringHandleRefUrl()
        });
    }
}

function toVerifiedUpdateEmailPage(dispatch, history, email) {
    dispatch(
        setVerifyEmailInfo({
            verifyEmailType: verifyEmailTypes.UPDATE_EMAIL,
            [queryStrKeys.UPDATE_EMAIL_TO]: email
        })
    );
    const { pathname } = window.location;
    if (pathname !== paths.EMAIL_VERIFY) {
        history.replace({
            pathname: paths.EMAIL_VERIFY,
            search: getQueryStringHandleRefUrl()
        });
    }
}

export function toCreateAccountPage(history) {
    const { pathname } = window.location;
    if (pathname !== paths.CREATE_ACCOUNT)
        history.replace({
            pathname: paths.CREATE_ACCOUNT,
            search: getQueryStringHandleRefUrl()
        });
}

function toCreateSponsorPage(history) {
    const { pathname } = window.location;
    if (pathname !== paths.CREATE_SPONSOR) {
        history.replace({
            pathname: paths.CREATE_SPONSOR,
            search: getQueryStringHandleRefUrl()
        });
    }
}

export function toThirdPartyOAuthInitFromEC(platform, search) {
    window.location.href = `${FEVER_API_ENDPOINT}/services/ec/oauth/${platform}/from/ec/init${search}`;
}

export function toThirdPartyOAuthInitFromFever(platform) {
    window.location.href = `${FEVER_API_ENDPOINT}/services/ec/oauth/${platform}/from/fever/init`;
    return `${FEVER_API_ENDPOINT}/services/ec/oauth/${platform}/from/fever/init`;
}

// 把 getSponsor 回傳的 403 錯誤描述對應成無權限頁的 reason。
// 兩種都不成立時回 undefined，讓無權限頁顯示預設文案並送一筆 Sentry（未預期的 403 組合）。
export function resolveSponsor403Reason(sponsorResult = {}) {
    if (sponsorResult.isRoleAuthoritiesFailed) {
        return permissionDeniedReasons.SPONSOR_NOT_FOUND;
    }
    if (sponsorResult.isRoleNoPermission) {
        return permissionDeniedReasons.ROLE_NO_PERMISSION;
    }
    return undefined;
}

// reason 為白名單值（config/permissionDeniedReasons），會出現在網址上讓使用者與客服
// 直接看得出被導轉的原因。不帶 reason 時行為與既有完全一致（顯示預設「無使用權限」文案）。
export function toPermissionNotDeniedPage(history, reason) {
    const { pathname } = window.location;
    if (pathname !== paths.PERMISSION_DENIED) {
        const searchParams = new URLSearchParams(getQueryStringHandleRefUrl());
        // 白名單過濾：呼叫端傳了未知值時寧可不帶，讓接收端當成「未預期導轉」送 Sentry
        if (isKnownPermissionDeniedReason(reason)) {
            searchParams.set('reason', reason);
        }
        history.replace({
            pathname: paths.PERMISSION_DENIED,
            search: searchParams.toString()
        });
    }
}

export function toOTPVerifyPage(history) {
    const { pathname } = window.location;
    if (pathname !== paths.OTP_VERIFY) {
        history.replace({
            pathname: paths.OTP_VERIFY,
            search: getQueryStringHandleRefUrl()
        });
    }
}

function getQueryStringHandleRefUrl() {
    const { href, pathname, search } = window.location;
    const { refUrl } = querystring.parse(search);
    const nextRefUrl = (() => {
        if (refUrl) {
            return refUrl;
        } else if (!pathsShouldNotSetAsRefUrl.has(pathname)) {
            return href;
        } else {
            return undefined;
        }
    })();
    return search.includes('merchant_id') ? search : querystring.stringify({ refUrl: nextRefUrl });
}
